import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Calendar, User, Building, MapPin, DollarSign, FileText, Download, Mail, Phone, Check, X, Plus, Eye } from 'lucide-react';
import AddCandidatureHSModal from './AddCandidatureHSModal';
import AddCandidatureManagerHSModal from './AddCandidatureManagerHSModal';
import CandidatureHSActionModal from './CandidatureHSActionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const ManagerHSOfferDetailPage = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [offer, setOffer] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNewCandidateModal, setShowNewCandidateModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    fetchOfferDetails();
    fetchCandidatures();
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offerData = await response.json();
        setOffer(offerData);
      } else {
        toast.error('Erreur lors du chargement de l\'offre');
        navigate('/manager-hs/offres-en-cours');
      }
    } catch (error) {
      console.error('Error fetching offer details:', error);
      toast.error('Erreur de connexion');
      navigate('/manager-hs/offres-en-cours');
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/job/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const candidaturesData = await response.json();
        setCandidatures(candidaturesData);
      }
    } catch (error) {
      console.error('Error fetching candidatures:', error);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToOffers = () => {
    navigate('/manager-hs/offres-en-cours');
  };

  const handleAddCandidature = () => {
    setShowNewCandidateModal(true);
  };

  const handleAction = (candidature, type) => {
    setSelectedCandidature(candidature);
    setActionType(type);
    setShowActionModal(true);
  };

  const handleCandidatureAdded = () => {
    setShowAddModal(false);
    fetchCandidatures();
  };

  const handleNewCandidateAdded = () => {
    setShowNewCandidateModal(false);
    fetchCandidatures();
  };

  const handleActionSuccess = () => {
    setShowActionModal(false);
    setSelectedCandidature(null);
    setActionType(null);
    fetchCandidatures();
  };

  const handleDownloadFile = async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/candidatures/download/${filename}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Erreur lors du téléchargement du fichier');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Erreur de connexion lors du téléchargement');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'accepte':
        return 'candidature-status-compact status-accepte';
      case 'refuse':
        return 'candidature-status-compact status-refuse';
      default:
        return 'candidature-status-compact status-nouveau';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepte':
        return 'Accepté';
      case 'refuse':
        return 'Refusé';
      default:
        return 'Nouveau';
    }
  };

  const companyLogos = {
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-destructive mb-4">Offre non trouvée</h1>
          <Button onClick={handleBackToOffers}>Retour aux offres</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-hs-offer-detail-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToOffers}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos['Horizon Solution']}
            alt="Logo Horizon Solution"
            className="rh-header-logo"
          />
        </div>
        <h1>Détail de l'offre - Horizon Solution</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {/* Offer Header */}
        <div className="offer-detail-header-compact">
          <h2>{offer.company} : {offer.title}</h2>
          <div className="offer-detail-info-line">
            <div className="offer-detail-info-item-compact">
              <User className="offer-detail-info-icon-compact" />
              <span>{offer.creator_name || 'Inconnu'}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <Calendar className="offer-detail-info-icon-compact" />
              <span>{formatDate(offer.date_arrivee)}</span>
            </div>
            <div className="offer-detail-info-separator">•</div>
            <div className="offer-detail-info-item-compact">
              <MapPin className="offer-detail-info-icon-compact" />
              <span>{offer.lieu_travail || 'Non spécifié'}</span>
            </div>
            {offer.type_poste && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <span>{offer.type_poste}</span>
                </div>
              </>
            )}
            {offer.taux_horaire && (
              <>
                <div className="offer-detail-info-separator">•</div>
                <div className="offer-detail-info-item-compact">
                  <DollarSign className="offer-detail-info-icon-compact" />
                  <span>{offer.taux_horaire}€/h</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Offer Description */}
        <Card className="offer-description-card-compact">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-3">Description détaillée</h3>
            <div className="offer-description-content-compact">
              <div className="mb-3">
                <h4 className="font-medium mb-1 text-sm text-primary">Missions</h4>
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.description}</p>
              </div>
              {offer.qualites_attendues && (
                <div>
                  <h4 className="font-medium mb-1 text-sm text-primary">Qualités attendues</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{offer.qualites_attendues}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Candidatures Table */}
        <Card className="candidatures-card-compact">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">
                Candidatures ({candidatures.length})
              </h3>
              <Button
                onClick={handleAddCandidature}
                className="btn-primary text-sm px-3 py-2"
                style={{ backgroundColor: '#eb5d2d', borderColor: '#eb5d2d' }}
              >
                <Plus size={16} className="mr-2" />
                Nouvelle candidature
              </Button>
            </div>
            <div className="candidatures-table-container-compact">
              <table className="candidatures-table-compact">
                <thead>
                  <tr>
                    <th>Candidat</th>
                    <th>Documents</th>
                    <th>Contact</th>
                    <th>Commentaire</th>
                    <th>Statut</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatures.map((candidature) => (
                    <tr key={candidature.id} className={candidature.status === 'refuse' ? 'candidature-refused' : ''}>
                      <td>
                        <div className="candidature-name-compact">
                          {candidature.prenom} {candidature.nom}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-files-compact">
                          {candidature.cv_filename ? (
                            <button
                              onClick={() => handleDownloadFile(candidature.cv_filename)}
                              className="document-download-btn"
                              title="Télécharger CV"
                            >
                              <Download size={12} />
                              CV
                            </button>
                          ) : (
                            <span className="document-unavailable">CV indisponible</span>
                          )}
                          {candidature.lettre_filename ? (
                            <button
                              onClick={() => handleDownloadFile(candidature.lettre_filename)}
                              className="document-download-btn"
                              title="Télécharger Lettre"
                            >
                              <Download size={12} />
                              LM
                            </button>
                          ) : (
                            <span className="document-unavailable">LM indisponible</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-contact-compact">
                          {candidature.email && (
                            <div className="contact-item-compact">
                              <Mail size={12} />
                              <span>{candidature.email}</span>
                            </div>
                          )}
                          {candidature.telephone && (
                            <div className="contact-item-compact">
                              <Phone size={12} />
                              <span>{candidature.telephone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-comment-compact">
                          {candidature.commentaire || '-'}
                          {candidature.raison_refus && (
                            <div className="refus-reason">
                              <strong>Refus:</strong> {candidature.raison_refus}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={getStatusStyle(candidature.status)}>
                          {getStatusText(candidature.status)}
                        </div>
                      </td>
                      <td>
                        <div className="candidature-actions-compact">
                          {candidature.status === 'accepte' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-1 text-xs px-2 py-1"
                              onClick={() => handleAction(candidature, 'visualiser')}
                            >
                              <Eye size={12} className="mr-1" />
                              Compléter
                            </Button>
                          ) : candidature.status === 'refuse' ? (
                            <span className="text-gray-400 text-xs">Refusé</span>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mr-1 text-xs px-2 py-1 btn-accept"
                                onClick={() => handleAction(candidature, 'accepte')}
                              >
                                <Check size={12} className="mr-1" />
                                Accepté
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs px-2 py-1 btn-refuse"
                                onClick={() => handleAction(candidature, 'refuse')}
                              >
                                <X size={12} className="mr-1" />
                                Refusé
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {candidatures.length === 0 && (
              <div className="empty-state-compact">
                <p>Aucune candidature pour cette offre</p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Candidature Modal */}
      {showAddModal && (
        <AddCandidatureHSModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleCandidatureAdded}
          jobOfferId={offerId}
        />
      )}

      {/* Action Modal */}
      {showActionModal && selectedCandidature && (
        <CandidatureHSActionModal
          isOpen={showActionModal}
          onClose={() => setShowActionModal(false)}
          onSuccess={handleActionSuccess}
          candidature={selectedCandidature}
          actionType={actionType}
        />
      )}

      {/* New Candidate Modal */}
      {showNewCandidateModal && (
        <AddCandidatureManagerHSModal
          isOpen={showNewCandidateModal}
          onClose={() => setShowNewCandidateModal(false)}
          onSuccess={handleNewCandidateAdded}
          offerId={offerId}
        />
      )}
    </div>
  );
};

export default ManagerHSOfferDetailPage;